// import core components
import { useEffect, useState, useRef, Fragment } from "react";
import { useRouter } from "next/router";
import { gsap } from "gsap/dist/gsap";
// import custom components
import Button from "@/elements/Shared/Button";
import LinkNav from "@/elements/LinkNav";

// import menu data
import menu from "src/data/layout/menu.json";

// import styles
import styles from "./menuMobile.module.scss";

// import functions
import setLockScreen from "@/utils/setLockScreen";

// import svg
import ChevronBottom from "@/elements/Shared/SVGs/ChevronBottom";
import ChevronTop from "@/elements/Shared/SVGs/ChevronTop";
import DistrictWeb from "@/elements/Shared/SVGs/DistrictWeb";

// import utils
import handleActiveLink from "@/utils/MenuMobile/handleActiveLink";
import handleClassName from "@/utils/handleClassName";
import handleHamburgerAnimation from "@/utils/MenuMobile/handleHamburgerAnimation";

type Props = {
  color: string;
};

function MenuMobile({ color }: Props) {
  // declare state
  const [toggleDropdown, setToggleDropdown] = useState(true);
  const [toggleMenu, setToggleMenu] = useState(false);

  // declare router
  const router = useRouter();

  // declare ref
  const toggleRef = useRef(null);
  const hamburgerTl = useRef(null);

  useEffect(() => {
    setLockScreen(toggleMenu);
  }, [toggleMenu]);

  useEffect(() => {
    if (process.browser) {
      hamburgerTl.current = handleHamburgerAnimation();
    }
  }, []);

  return (
    <nav
      id="nav-mobile"
      className={`${styles._nav} ${toggleMenu ? styles["menu--open"] : ""}`}
    >
      <div
        className={`container ${styles._top_container} ${
          styles[`_color_${color}`]
        }
        `}
      >
        <LinkNav
          path="/"
          ariaLabel="District web page d'accueil"
          className={styles._logo_district_web}
          setToggle={() => {
            if (toggleMenu) toggleRef.current.click();
          }}
        >
          <DistrictWeb color={color} />
        </LinkNav>
        <input
          name="hamburger-menu-toggler"
          aria-label="menu-toggler"
          ref={toggleRef}
          className={handleClassName(["hamburger--toggle"], styles)}
          type="checkbox"
          onClick={() => {
            setToggleMenu(!toggleMenu);

            if (!toggleDropdown) setToggleDropdown(true);
            if (toggleDropdown && toggleMenu) setToggleDropdown(false);

            if (hamburgerTl.current.paused()) hamburgerTl.current.resume();
            else if (hamburgerTl.current.reversed()) hamburgerTl.current.play();
            else {
              hamburgerTl.current.reverse(0.5);
            }
          }}
        />
        <div className={handleClassName(["hamburger--container"], styles)}>
          <div
            id="hamburger--inner-container"
            className={handleClassName(["hamburger"], styles)}
          >
            <div
              id="hamburger--line-1"
              className={`hamburger--bar ${handleClassName(
                ["hamburger--bar", "hamburger--bar-1"],
                styles
              )} hamburger--bar`}
            ></div>
            <div
              id="hamburger--line-2"
              className={`hamburger--bar ${handleClassName(
                ["hamburger--bar", "hamburger--bar-2"],
                styles
              )} hamburger--bar`}
            ></div>
            <div
              id="hamburger--line-3"
              className={`hamburger--bar ${handleClassName(
                ["hamburger--bar", "hamburger--bar-3"],
                styles
              )} hamburger--bar`}
            ></div>
          </div>
        </div>

        <div className={`${styles._container} ${styles._color_white}`}>
          <ul
            className={`${styles._menu_list} ${
              toggleDropdown ? styles._dropdown_container_open : ""
            }`}
          >
            {menu.menus.map((menu) => {
              if (menu.children.length > 0) {
                return (
                  <Fragment key={menu.title}>
                    <li
                      className={`${styles["menu-mobile-items"]} ${
                        styles._dropdown
                      } ${handleActiveLink(router, menu.lien, styles)}`}
                    >
                      <span className={styles["menu-mobile-items-link"]}>
                        <LinkNav
                          path={menu.lien}
                          setToggle={() => {
                            if (toggleMenu) toggleRef.current.click();
                          }}
                        >
                          <>{menu.title}</>
                        </LinkNav>

                        <span
                          className={styles._toggle}
                          onClick={() => {
                            setToggleDropdown(!toggleDropdown);
                          }}
                        >
                          {!toggleDropdown ? <ChevronBottom /> : <ChevronTop />}
                        </span>
                      </span>

                      <ul
                        className={`${styles._dropdown_container} ${
                          toggleDropdown ? styles._open : ""
                        }`}
                      >
                        {menu.children.map((child, i) => {
                          return (
                            <li
                              key={child.title}
                              className={`${handleActiveLink(
                                router,
                                child.lien,
                                styles
                              )} ${styles["dropdown--items"]}`}
                            >
                              <LinkNav
                                path={child.lien}
                                setToggle={() => {
                                  if (toggleMenu) toggleRef.current.click();
                                }}
                                setToggleDropdown={setToggleDropdown}
                              >
                                {child.title}
                              </LinkNav>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  </Fragment>
                );
              } else {
                return (
                  <Fragment key={menu.title}>
                    <li
                      className={`${
                        styles["menu-mobile-items"]
                      } ${handleActiveLink(router, menu.lien, styles)}`}
                    >
                      <LinkNav
                        className={styles["menu-mobile-items-link"]}
                        path={menu.lien}
                        setToggle={() => {
                          if (toggleMenu) toggleRef.current.click();
                        }}
                      >
                        {menu.title}
                      </LinkNav>
                    </li>
                  </Fragment>
                );
              }
            })}
          </ul>

          <Button
            className={styles["contact-btn-mobile"]}
            path="/contact"
            bg={"transparent"}
            color={"white"}
            border={"white"}
            icon={false}
            setToggle={() => {
              if (toggleMenu) toggleRef.current.click();
            }}
            isFrontAbosolute={true}
          >
            Contact
          </Button>
        </div>
      </div>

      <div
        id="separator"
        className={`${styles._separator} ${
          color === "black" ? styles._alternative : ""
        }`}
      ></div>
    </nav>
  );
}

export default MenuMobile;
